import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Play, RefreshCw, ChevronDown, Save, AlertTriangle, CloudDownload, Sparkles, Send, Megaphone, X, Users } from "lucide-react";
import { useFb } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const OVERRIDE_FIELDS = [
  ["minutes_played", "Mins"],
  ["goals", "Goals"],
  ["assists", "Assists"],
  ["saves", "Saves"],
  ["penalties_saved", "Pen Saved"],
  ["yellow_cards", "Yellow"],
  ["red_cards", "Red"],
  ["own_goals", "Own Goals"],
  ["penalties_missed", "Pen Miss"],
];

const PerfEditor = ({ perf, onSaved }) => {
  const fb = useFb();
  const ov = perf.manual_override?.override_values || {};
  const [vals, setVals] = useState(() => {
    const v = {};
    OVERRIDE_FIELDS.forEach(([k]) => { v[k] = ov[k] ?? perf[k] ?? 0; });
    return v;
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reason.trim()) { toast.error("Add a reason for the override"); return; }
    setBusy(true);
    try {
      await fb.post("/football/admin/scoring/override", {
        performance_id: perf.id,
        override_values: Object.fromEntries(OVERRIDE_FIELDS.map(([k]) => [k, Number(vals[k]) || 0])),
        reason: reason.trim(),
      });
      toast.success(`Override applied — fixture rescored`);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Override failed (soft-lock may have expired)"); }
    setBusy(false);
  };

  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid={`perf-editor-${perf.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-bold">{perf.player_name || perf.name || perf.player_id} <span className="text-xs text-muted-foreground">· {perf.position} · {perf.team_name}</span></div>
        <div className="text-xs font-extrabold wc-gold">{perf.fantasy_points_raw ?? 0} pts</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {OVERRIDE_FIELDS.map(([k, label]) => (
          <label key={k} className="text-[11px] text-muted-foreground">
            {label}
            <input type="number" value={vals[k]} onChange={(e) => setVals({ ...vals, [k]: e.target.value })}
              className="mt-0.5 w-full rounded border border-border bg-card px-2 py-1 text-sm text-foreground" data-testid={`perf-${perf.id}-${k}`} />
          </label>
        ))}
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for override (required)"
        className="mt-2 w-full rounded border border-border bg-card px-2 py-1.5 text-sm" data-testid={`perf-${perf.id}-reason`} />
      <button onClick={save} disabled={busy} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg wc-bg-gold py-1.5 text-sm font-extrabold text-slate-900 disabled:opacity-50" data-testid={`perf-${perf.id}-save`}>
        <Save size={14} /> {busy ? "Saving…" : "Apply Override"}
      </button>
    </div>
  );
};

const FixtureCard = ({ fx, onRan }) => {
  const fb = useFb();
  const [open, setOpen] = useState(false);
  const [perfs, setPerfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [repulling, setRepulling] = useState(false);
  const finished = ["FT", "AET", "PEN"].includes(fx.status);

  const loadPerfs = useCallback(async () => {
    setLoading(true);
    try { const d = await fb.get(`/football/admin/fixture/${fx.id}/performances`); setPerfs(d.performances || []); } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fx.id]);

  const toggle = async () => { const n = !open; setOpen(n); if (n && perfs.length === 0) await loadPerfs(); };

  const runScoring = async () => {
    setRunning(true);
    try { const r = await fb.post(`/football/admin/scoring/run/${fx.id}`); toast.success(`Scored ${r.squads_scored} squads, ${r.performances_updated} performances`); onRan?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not run scoring"); }
    setRunning(false);
  };

  const repull = async () => {
    setRepulling(true);
    try { const r = await fb.post(`/football/admin/scoring/repull/${fx.id}`); toast.success(`Re-pulled ${r.player_rows_pulled} rows · rescored`); onRan?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Re-pull failed"); }
    setRepulling(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3.5" data-testid={`admin-fixture-${fx.id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold">{fx.home_team?.name || "TBD"} {finished && <span className="wc-gold">{fx.home_score ?? 0}</span>} v {finished && <span className="wc-gold">{fx.away_score ?? 0}</span>} {fx.away_team?.name || "TBD"}</div>
          <div className="text-[11px] text-muted-foreground">Status: {fx.status} · Scoring: {fx.scoring_run_status || "not run"}</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={runScoring} disabled={running || !finished} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg wc-bg-gold py-1.5 text-xs font-extrabold text-slate-900 disabled:opacity-40" data-testid={`run-scoring-${fx.id}`}>
          <Play size={13} /> {running ? "Running…" : "Run Scoring"}
        </button>
        <button onClick={repull} disabled={repulling || !finished} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-secondary py-1.5 text-xs font-bold disabled:opacity-40" data-testid={`repull-${fx.id}`}>
          <RefreshCw size={13} /> {repulling ? "Re-pulling…" : "Re-pull"}
        </button>
        <button onClick={toggle} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-secondary py-1.5 text-xs font-bold" data-testid={`toggle-perfs-${fx.id}`}>
          <ChevronDown size={13} className={open ? "rotate-180 transition-transform" : "transition-transform"} /> Overrides
        </button>
      </div>
      {!finished && <div className="mt-2 flex items-center gap-1 text-[11px] text-amber-400"><AlertTriangle size={12} /> Fixture not finished — scoring disabled</div>}
      {open && (
        <div className="mt-3 space-y-2">
          {loading ? <div className="h-20 animate-pulse rounded-lg bg-secondary/50" /> :
            perfs.length === 0 ? <div className="py-3 text-center text-xs text-muted-foreground">No performances ingested for this fixture yet.</div> :
              perfs.map((p) => <PerfEditor key={p.id} perf={p} onSaved={loadPerfs} />)}
        </div>
      )}
    </div>
  );
};

export default function AdminFootballMatchDayPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const [days, setDays] = useState([]);
  const [md, setMd] = useState("");
  const [fixtures, setFixtures] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [pgenBusy, setPgenBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [bcOpen, setBcOpen] = useState(false);
  const [bcTitle, setBcTitle] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [bcBusy, setBcBusy] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [revenue, setRevenue] = useState(null);

  const regenPredictor = async () => {
    setPgenBusy(true);
    try {
      const r = await fb.post("/football/admin/predictor/generate");
      toast.success(`Predictor: +${r.created ?? 0} new · ${r.total_questions ?? 0} total · ${(r.days || []).length} days`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate questions"); }
    setPgenBusy(false);
  };

  const sendTestPush = async () => {
    setTestBusy(true);
    try {
      const r = await fb.post("/admin/push/test", { user_id: dbUser?.id, title: "APL Test", body: "Test push from admin" });
      if (r.success) toast.success(`Test push sent to ${r.sent_to || "you"}`);
      else toast.error(`Push failed: ${r.error || "unknown"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test push failed"); }
    setTestBusy(false);
  };

  const sendBroadcast = async () => {
    if (!bcTitle.trim() || !bcBody.trim()) { toast.error("Title and body are required"); return; }
    setBcBusy(true);
    try {
      const r = await fb.post("/admin/push/broadcast", { title: bcTitle.trim(), body: bcBody.trim(), target: "all" });
      toast.success(`Broadcast: ${r.sent ?? 0} sent · ${r.failed ?? 0} failed (${r.total_tokens ?? 0} tokens)`);
      setBcOpen(false); setBcTitle(""); setBcBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    setBcBusy(false);
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await fb.post("/football/admin/sync/run?target=all", {});
      setSyncResult(r);
      toast.success(`Synced: ${r.players ?? 0} players · ${r.fixtures ?? 0} fixtures · ${r.teams ?? 0} teams`);
      // reload match days + fixtures with the fresh data
      try { const d = await fb.get("/football/match-days"); setDays(d.match_days || []); if ((d.match_days || []).length) setMd(d.match_days[d.match_days.length - 1]); } catch { /* ignore */ }
      await loadFixtures();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — check the API-Football key/plan");
    }
    setSyncing(false);
  };

  useEffect(() => {
    (async () => {
      try { const d = await fb.get("/football/match-days"); setDays(d.match_days || []); if ((d.match_days || []).length) setMd(d.match_days[d.match_days.length - 1]); } catch { /* ignore */ }
      try { setTokenInfo(await fb.get("/admin/push/token-count")); } catch { /* ignore */ }
      try { setRevenue(await fb.get("/football/admin/consumables/aggregate")); } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFixtures = useCallback(async () => {
    if (!md) return;
    setLoading(true);
    try { const d = await fb.get("/football/fixtures", { match_day: md }); setFixtures(d.fixtures || []); } catch { /* ignore */ }
    try { const r = await fb.get("/football/admin/scoring/runs", { limit: 10 }); setRuns(r.runs || []); } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [md]);

  useEffect(() => { loadFixtures(); }, [loadFixtures]);

  if (dbUser && !dbUser.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="admin-football-matchday-page">
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/admin")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="admin-fb-back"><ChevronLeft size={18} /> Admin</button>
        <h1 className="text-xl font-extrabold text-white">Football — Match Day Control</h1>
        <p className="mt-1 text-sm text-white/70">Trigger scoring runs and apply live stat overrides.</p>
      </div>

      <div className="mx-auto max-w-md px-4">
        {/* Force-sync from API-Football: clears the quota-guard timestamps and re-pulls
            fixtures + teams + players. Use this to populate a fresh/empty database. */}
        <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3" data-testid="admin-fb-sync-card">
          <button onClick={runSync} disabled={syncing} className="flex w-full items-center justify-center gap-2 rounded-lg wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900 disabled:opacity-50" data-testid="admin-fb-sync-now">
            <CloudDownload size={16} className={syncing ? "animate-pulse" : ""} /> {syncing ? "Syncing from API-Football…" : "Sync players & fixtures now"}
          </button>
          {syncResult && (
            <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="admin-fb-sync-result">
              ✓ {syncResult.players ?? 0} players · {syncResult.fixtures ?? 0} fixtures · {syncResult.teams ?? 0} teams
            </p>
          )}
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">Run this once after deploying to populate the live database.</p>
        </div>

        {/* Launch-ops tools */}
        <div className="mt-3 grid grid-cols-1 gap-2" data-testid="admin-fb-tools">
          <button onClick={() => navigate("/admin/football/users")} className="flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 py-2.5 text-sm font-bold text-amber-300" data-testid="admin-fb-users-link">
            <Users size={16} /> 👥 Football Users & Consumables
          </button>
          {tokenInfo && (
            <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5 text-sm" data-testid="admin-fb-token-count">
              <span className="font-bold text-emerald-300">📲 Active push tokens: {tokenInfo.active_tokens}</span>
              <span className={`text-[11px] font-bold ${tokenInfo.firebase_initialized ? "text-emerald-400" : "text-red-400"}`}>
                {tokenInfo.firebase_initialized ? "FCM ready" : "FCM not initialized"}
              </span>
            </div>
          )}
          <button onClick={regenPredictor} disabled={pgenBusy} className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card py-2.5 text-sm font-bold disabled:opacity-50" data-testid="admin-fb-regen-predictor">
            <Sparkles size={16} className={pgenBusy ? "animate-pulse" : "text-amber-400"} /> {pgenBusy ? "Regenerating…" : "🔄 Regenerate Predictor Questions"}
          </button>
          <button onClick={sendTestPush} disabled={testBusy} className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card py-2.5 text-sm font-bold disabled:opacity-50" data-testid="admin-fb-test-push">
            <Send size={16} className={testBusy ? "animate-pulse" : "text-emerald-400"} /> {testBusy ? "Sending…" : "📲 Send Test Push to Me"}
          </button>
          <button onClick={() => setBcOpen(true)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 py-2.5 text-sm font-bold text-red-300" data-testid="admin-fb-broadcast-open">
            <Megaphone size={16} /> 📢 Broadcast Push to All Users
          </button>
        </div>

        {/* Consumables revenue (Issue 4C) */}
        {revenue && (
          <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3.5" data-testid="admin-fb-revenue-card">
            <h2 className="mb-2 text-sm font-extrabold text-emerald-300">💰 Consumables Revenue ({revenue.currency})</h2>
            <div className="grid grid-cols-3 gap-2">
              {[["last_7d", "7 days"], ["last_30d", "30 days"], ["all_time", "All time"]].map(([k, lbl]) => (
                <div key={k} className="rounded-lg border border-border bg-background p-2 text-center" data-testid={`admin-fb-rev-${k}`}>
                  <div className="text-base font-extrabold wc-gold">{revenue.revenue?.[k] ?? 0}</div>
                  <div className="text-[10px] text-muted-foreground">{lbl}</div>
                </div>
              ))}
            </div>
            {revenue.top_spenders?.length > 0 && (
              <div className="mt-3">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Top Spenders</p>
                <div className="space-y-1" data-testid="admin-fb-top-spenders">
                  {revenue.top_spenders.slice(0, 5).map((s, i) => (
                    <div key={s.user_id} className="flex items-center justify-between text-xs">
                      <span className="truncate pr-2">{i + 1}. {s.display_name}</span>
                      <span className="font-bold text-emerald-300">AED {s.spend_aed}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {revenue.most_used_per_day?.length > 0 && (
              <div className="mt-3">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Most-Used / Match Day</p>
                <div className="space-y-1" data-testid="admin-fb-most-used">
                  {revenue.most_used_per_day.slice(0, 5).map((d) => (
                    <div key={d.match_day} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{d.match_day}</span>
                      <span className="font-bold">{d.top_label || "—"}{d.top_count ? ` ×${d.top_count}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <select value={md} onChange={(e) => setMd(e.target.value)} className="flex-1 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-bold" data-testid="admin-fb-day-select">
            {days.length === 0 && <option value="">No match days</option>}
            {days.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button onClick={loadFixtures} className="rounded-xl bg-secondary p-2.5" data-testid="admin-fb-refresh"><RefreshCw size={18} /></button>
        </div>

        {loading ? <div className="mt-5 h-40 animate-pulse rounded-xl bg-secondary/50" /> : (
          <div className="mt-4 space-y-2">
            {fixtures.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="admin-fb-no-fixtures">No fixtures for this match day.</div> :
              fixtures.map((fx) => <FixtureCard key={fx.id} fx={fx} onRan={loadFixtures} />)}
          </div>
        )}

        {runs.length > 0 && (
          <div className="mt-6 rounded-xl border border-border bg-card p-4" data-testid="admin-fb-runs">
            <h2 className="mb-2 text-sm font-bold">Recent Scoring Runs</h2>
            <div className="space-y-1.5">
              {runs.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs">
                  <span className="truncate pr-2 text-muted-foreground">{r.triggered_by} · {r.match_day}</span>
                  <span className={`font-bold ${r.status === "completed" ? "text-emerald-400" : r.status === "failed" ? "text-red-400" : "text-amber-400"}`}>{r.status} · {r.squads_scored ?? 0} sq</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {bcOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" data-testid="admin-fb-broadcast-modal">
          <div className="w-full max-w-sm rounded-2xl border border-red-500/30 bg-slate-800 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-white"><Megaphone size={18} className="text-red-400" /> Broadcast Push</h2>
              <button onClick={() => setBcOpen(false)} className="rounded-full p-1 text-slate-400 hover:text-white" data-testid="admin-fb-broadcast-close"><X size={20} /></button>
            </div>
            <label className="text-xs font-bold text-slate-400">Title <span className="text-slate-500">({bcTitle.length}/50)</span></label>
            <input value={bcTitle} onChange={(e) => setBcTitle(e.target.value.slice(0, 50))} maxLength={50} placeholder="e.g. ⚽ World Cup is live!"
              className="mt-1 w-full rounded-lg border border-border bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-amber-500" data-testid="admin-fb-broadcast-title" />
            <label className="mt-3 block text-xs font-bold text-slate-400">Body <span className="text-slate-500">({bcBody.length}/150)</span></label>
            <textarea value={bcBody} onChange={(e) => setBcBody(e.target.value.slice(0, 150))} maxLength={150} rows={3} placeholder="Build your squad before kickoff…"
              className="mt-1 w-full rounded-lg border border-border bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-amber-500" data-testid="admin-fb-broadcast-body" />
            <div className="mt-4 flex gap-2">
              <button onClick={() => setBcOpen(false)} className="flex-1 rounded-xl border border-slate-600 py-2.5 text-sm font-bold text-slate-300" data-testid="admin-fb-broadcast-cancel">Cancel</button>
              <button
                onClick={() => { if (window.confirm("Send this push to ALL users with active push tokens. Continue?")) sendBroadcast(); }}
                disabled={bcBusy || !bcTitle.trim() || !bcBody.trim()}
                className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-extrabold text-white disabled:opacity-40"
                data-testid="admin-fb-broadcast-send"
              >
                {bcBusy ? "Sending…" : "Send to All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
